import { useEffect, useRef, useState } from "react";
import { CheckSquare, Square, Plus, Trash2, Loader2, MapPin, Camera, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "../../lib/api";

// Ejecución del ticket en campo: checklist, check-in con geocerco y evidencia
// fotográfica mobile-first.
export default function FieldTicketOps({ moduleKey, item, onRefresh }) {
  const [items, setItems] = useState(item.checklist || []);
  const [text, setText] = useState("");
  const [checkins, setCheckins] = useState(item.checkins || []);
  const [checkouts, setCheckouts] = useState(item.checkouts || []);
  const [locating, setLocating] = useState(false);
  const [outBusy, setOutBusy] = useState(false);
  const [upBusy, setUpBusy] = useState(false);
  const camRef = useRef(null);

  useEffect(() => { setItems(item.checklist || []); setCheckins(item.checkins || []); setCheckouts(item.checkouts || []); }, [item.id]);

  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  const persist = async (next) => {
    setItems(next);
    try { await api.put(`/field/tickets/${item.id}/checklist`, { items: next }); }
    catch { toast.error("Error al guardar checklist"); }
  };
  const addItem = () => { if (!text.trim()) return; persist([...items, { text: text.trim(), done: false }]); setText(""); };
  const toggle = async (idx) => {
    try { const { data } = await api.post(`/field/tickets/${item.id}/checklist/toggle`, { index: idx }); setItems(data.checklist); }
    catch { toast.error("Error"); }
  };
  const rm = (idx) => persist(items.filter((_, i) => i !== idx));

  const checkin = () => {
    if (!navigator.geolocation) { toast.error("Tu dispositivo no soporta geolocalización"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/field/tickets/${item.id}/checkin`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCheckins((c) => [...c, data]);
        toast[data.dentro ? "success" : "error"](data.dentro ? `Check-in DENTRO del geocerco (${data.distancia_m} m)` : `Estás FUERA del sitio (${data.distancia_m} m > ${data.radio_m} m)`);
        onRefresh && onRefresh();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setLocating(false); }
    }, () => { toast.error("No se pudo obtener tu ubicación"); setLocating(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const checkout = () => {
    if (!navigator.geolocation) { toast.error("Sin geolocalización"); return; }
    setOutBusy(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.post(`/field/tickets/${item.id}/checkout`, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setCheckouts((c) => [...c, data]);
        toast.success(`Check-out · tiempo en sitio ${data.tiempo_min} min`);
        onRefresh && onRefresh();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setOutBusy(false); }
    }, () => { toast.error("No se pudo obtener tu ubicación"); setOutBusy(false); }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/mod/${moduleKey}/items/${item.id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidencia subida");
      onRefresh && onRefresh();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error al subir"); }
    finally { setUpBusy(false); if (camRef.current) camRef.current.value = ""; }
  };

  const fotos = (item.files || []).filter((f) => /image\//.test(f.mime || "") || /\.(png|jpe?g|webp|gif)$/i.test(f.name || ""));

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-5" data-testid="field-ticket-ops">
      {/* Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 overline text-navy/40"><CheckSquare className="w-3.5 h-3.5" /> Checklist de ejecución</div>
          <span className="text-xs text-navy/50">{done}/{items.length} · {pct}%</span>
        </div>
        <div className="h-1.5 bg-navy/10 rounded-full mb-3 overflow-hidden"><div className="h-full bg-gradient-violet transition-all" style={{ width: `${pct}%` }} /></div>
        <div className="space-y-1.5 mb-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`checklist-item-${i}`}>
              <button onClick={() => toggle(i)} className="text-violet" data-testid={`checklist-toggle-${i}`}>
                {it.done ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4 text-navy/30" />}
              </button>
              <span className={`text-sm flex-1 ${it.done ? "line-through text-navy/40" : ""}`}>{it.text}</span>
              <button onClick={() => rm(i)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder="Agregar paso…" className="field-sm flex-1" data-testid="checklist-input" />
          <button onClick={addItem} className="btn btn-outline text-sm" data-testid="checklist-add"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Geofence check-in */}
      <div>
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><MapPin className="w-3.5 h-3.5" /> Check-in / Check-out con geocerco</div>
        <div className="flex gap-2">
          <button onClick={checkin} disabled={locating} className="btn btn-primary text-sm flex-1" data-testid="geo-checkin">
            {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Entrada
          </button>
          <button onClick={checkout} disabled={outBusy || checkins.length === 0} className="btn btn-outline text-sm flex-1" data-testid="geo-checkout">
            {outBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Salida
          </button>
        </div>
        {checkouts.length > 0 && (
          <div className="mt-2 text-xs text-navy/60 bg-cream border border-navy/10 rounded-lg px-3 py-1.5" data-testid="checkout-last">
            Último tiempo en sitio: <b>{checkouts.at(-1).tiempo_min} min</b>
          </div>
        )}
        {checkins.length > 0 && (
          <div className="mt-2 space-y-1">
            {checkins.slice(-4).reverse().map((c, i) => (
              <div key={i} className="text-xs flex items-center gap-2 bg-cream border border-navy/10 rounded-lg px-3 py-1.5" data-testid={`checkin-${i}`}>
                {c.dentro ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <XCircle className="w-3.5 h-3.5 text-destructive" />}
                <span>{new Date(c.at).toLocaleString("es-MX")}</span>
                <span className="ml-auto text-navy/50">{c.distancia_m} m {c.dentro ? "· dentro" : `> ${c.radio_m} m`}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile-first evidence */}
      <div>
        <div className="flex items-center gap-2 overline text-navy/40 mb-2"><Camera className="w-3.5 h-3.5" /> Evidencia fotográfica</div>
        <button onClick={() => camRef.current?.click()} disabled={upBusy} className="btn btn-outline text-sm w-full" data-testid="evidence-capture">
          {upBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Tomar / subir foto
        </button>
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="evidence-input" />
        {fotos.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-2">
            {fotos.map((f) => (
              <a key={f.id} href={mediaUrl(f.url)} target="_blank" rel="noreferrer" className="block">
                <img src={mediaUrl(f.url)} alt={f.name} className="w-full h-16 object-cover rounded-lg border border-navy/10" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
